import { IconButton } from "@carbon6/c6-frontend-design";
import type { ReactNode } from "react";
import type { FolderScanResult } from "@/upload";
import type { UploadEngine, UploadFileState, UploadJobView } from "@/upload";
import { formatUploadBytes } from "@/page/datasetUpload/datasetUploadFormatters";
import { EmptyState } from "@/shared/ui/emptyState/public";
import styles from "./uploadWindow.module.css";

// 우측 목록 아이템(스캔 미리보기와 파일 전송에 공통). category 는 필터에 쓴다.
export type ItemCategory = "active" | "done" | "failed";
export type UploadListItem = {
  key: string;
  label: string;
  statusText: string;
  category: ItemCategory;
  sizeBytes: number | null;
  errorMessage: string | null;
  onRetry: (() => void) | null;
};

export type ListFilter = "all" | "active" | "failed";

/**
 * 우측 목록의 필터 띠. 목록 페인이 아니라 2단 그리드의 첫 행(우측 칸)에 둔다. 좌측 칸을
 * 비워야 폼 첫 필드와 목록 상자가 같은 높이에서 시작한다. 그래서 두 칸을 모두 아는
 * 상위(UploadWindowContent)가 상태를 소유한다. 이 컴포넌트는 렌더만 한다.
 */
export function UploadItemFilterRow({
  filter,
  failedCount,
  onFilterChange,
}: {
  filter: ListFilter;
  // 상위가 이미 집계한 실패 수. 요약 카운터와 같은 값이다.
  failedCount: number;
  onFilterChange: (filter: ListFilter) => void;
}): ReactNode {
  return (
    <div className={styles.filterRow} role="group" aria-label="목록 필터">
      <FilterButton current={filter} value="all" label="전체" onSelect={onFilterChange} />
      <FilterButton current={filter} value="active" label="완료 숨김" onSelect={onFilterChange} />
      <FilterButton
        current={filter}
        value="failed"
        label={failedCount > 0 ? `실패만 (${failedCount})` : "실패만"}
        onSelect={onFilterChange}
      />
    </div>
  );
}

// 우측 파일 목록. setup(스캔 미리보기)과 active(전송)가 같은 컴포넌트를 쓴다. 재시도만 아이템
// 데이터에 따라 달라진다. 아이템과 필터 선택은 상위가 전달한다.
//
// 목록 페인(`.listPane`)은 상위가 소유한다. 하단 상태 바가 이 목록의 형제로 같은 페인에 있기
// 때문이다. 필터 띠를 페인 밖에 둔 이유도 같다. 여기서는 목록만 그린다.
export function UploadItemList({
  items,
  filter,
  emptyHint,
}: {
  items: readonly UploadListItem[];
  filter: ListFilter;
  // 아이템이 없을 때 표시할 안내(예: 폴더 선택 전).
  emptyHint?: string;
}): ReactNode {
  const visible = items.filter((item) => matchesFilter(item, filter));

  return (
    <ul className={styles.itemList} aria-label="업로드 항목">
      {items.length === 0 ? (
        <li className={styles.emptyRow}>
          <EmptyState>{emptyHint ?? "표시할 항목이 없습니다."}</EmptyState>
        </li>
      ) : visible.length === 0 ? (
        <li className={styles.emptyRow}>
          <EmptyState>
            {filter === "failed" ? "실패한 항목이 없습니다." : "표시할 항목이 없습니다."}
          </EmptyState>
        </li>
      ) : (
        visible.map((item) => (
          <li key={item.key} className={styles.itemRow} data-category={item.category}>
            <span className={styles.itemLabel} title={item.errorMessage ?? item.label}>
              {item.label}
            </span>
            <span className={styles.itemStatus} data-category={item.category}>
              {item.statusText}
            </span>
            <span className={styles.itemSize}>
              {item.sizeBytes === null ? "" : formatUploadBytes(item.sizeBytes)}
            </span>
            {item.onRetry !== null ? (
              <IconButton text="재시도" size="sm" variant="surface" onClick={item.onRetry} />
            ) : (
              <span className={styles.retrySlot} aria-hidden="true" />
            )}
          </li>
        ))
      )}
    </ul>
  );
}

function FilterButton({
  current,
  value,
  label,
  onSelect,
}: {
  current: ListFilter;
  value: ListFilter;
  label: string;
  onSelect: (value: ListFilter) => void;
}): ReactNode {
  return (
    <IconButton
      text={label}
      size="sm"
      variant="surface"
      selected={current === value}
      onClick={() => onSelect(value)}
    />
  );
}

function matchesFilter(item: UploadListItem, filter: ListFilter): boolean {
  if (filter === "all") {
    return true;
  }
  if (filter === "failed") {
    return item.category === "failed";
  }
  // active: 완료 아이템을 숨긴다. 진행 중과 실패만 남긴다.
  return item.category !== "done";
}

// setup 미리보기. 스캔된 파일을 "대기" 아이템으로 만든다(재시도 없음). 폴더를 선택하지 않았으면 빈 목록이다.
export function scanToItems(scan: FolderScanResult | null): UploadListItem[] {
  if (scan === null) {
    return [];
  }
  return scan.files.map((file) => ({
    key: `scan:${file.relativePath}`,
    label: file.relativePath,
    statusText: "업로드를 기다리고 있습니다.",
    category: "active",
    sizeBytes: file.sizeBytes,
    errorMessage: null,
    onRetry: null,
  }));
}

// active: 전송 중인 파일 목록. 전송이 끝난 뒤에 남은 실패 아이템에만 재시도 콜백을 붙인다.
// 엔진은 failed 잡의 재시도만 받는다. 그래서 전송 중에는 버튼을 표시하지 않는다.
//
// finalize(완료 처리)는 여기에 넣지 않는다. 잡 상태 문구가 이미 같은 단계를 표시한다.
// `completing` 문구가 finalize running 에 해당하고, finalize 가 실패하면 잡은 failed 가 된다.
// 하단 상태 바가 그 재시도도 맡는다. 목록에 파일만 담아야 "파일 n/m 완료" 의 분모가 실제 파일 수가 된다.
export function activeToItems(job: UploadJobView, engine: UploadEngine): UploadListItem[] {
  const retryable = job.status === "failed";
  return job.files.map((file) => ({
    key: `file:${file.relativePath}`,
    label: file.relativePath,
    statusText: fileStatusText(file),
    category: fileCategory(file.status),
    sizeBytes: file.sizeBytes,
    errorMessage: file.errorMessage,
    onRetry:
      retryable && file.status === "failed"
        ? () => engine.retryFile(job.id, file.relativePath)
        : null,
  }));
}

export function countItems(items: readonly UploadListItem[]): { done: number; failed: number } {
  return {
    done: items.filter((item) => item.category === "done").length,
    failed: items.filter((item) => item.category === "failed").length,
  };
}

function fileCategory(status: UploadFileState["status"]): ItemCategory {
  if (status === "failed") {
    return "failed";
  }
  if (status === "uploaded" || status === "skipped") {
    return "done";
  }
  return "active";
}

function fileStatusText(file: UploadFileState): string {
  switch (file.status) {
    case "pending":
      return "업로드를 기다리고 있습니다.";
    case "uploading":
      return "파일을 업로드하고 있습니다.";
    case "uploaded":
      return "파일 업로드를 완료했습니다.";
    case "skipped":
      return "이미 업로드된 파일입니다.";
    case "failed":
      return file.errorMessage ?? "파일 업로드에 실패했습니다. 다시 시도해 주세요.";
  }
}
